import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  Card,
  Row,
  Col,
  Input,
  Modal,
  Form,
  Typography,
  Popconfirm,
  message,
  Tooltip,
  Descriptions,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getUserLoginDetails,
} from "./services/userApi";
import { useUserContext } from "../../../Contexts/user";
import "./styles.css";

const { Title, Text } = Typography;
const { Search } = Input;

const Users = () => {
  const { userData } = useUserContext();
  const isAdmin = userData?.role === "admin";

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Create/Edit modal
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm] = Form.useForm();
  const [userModalLoading, setUserModalLoading] = useState(false);

  // Export login details
  const [exportingLogins, setExportingLogins] = useState(false);

  // Login details modal
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginDetails, setLoginDetails] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginUserName, setLoginUserName] = useState("");

  // ---- Data fetching ----

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      const keyed = data.map((u, i) => ({ ...u, key: u.id || i }));
      setUsers(keyed);
      setFilteredUsers(keyed);
    } catch {
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ---- Search ----

  useEffect(() => {
    if (!searchText) {
      setFilteredUsers(users);
      return;
    }
    const lower = searchText.toLowerCase();
    setFilteredUsers(
      users.filter(
        (u) =>
          u.name?.toLowerCase().includes(lower) ||
          u.email?.toLowerCase().includes(lower) ||
          u.phone?.toLowerCase().includes(lower) ||
          u.company?.toLowerCase().includes(lower)
      )
    );
  }, [searchText, users]);

  // ---- Create / Edit user ----

  const openCreateModal = () => {
    setEditingUser(null);
    userForm.resetFields();
    setUserModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingUser(record);
    userForm.setFieldsValue({
      name: record.name,
      email: record.email,
      phone: record.phone || undefined,
      company: record.company || undefined,
    });
    setUserModalOpen(true);
  };

  const handleUserSubmit = async () => {
    try {
      const values = await userForm.validateFields();
      setUserModalLoading(true);
      if (editingUser) {
        await updateUser(editingUser.id, values);
        message.success("User updated successfully");
      } else {
        await createUser(values);
        message.success("User created successfully");
      }
      setUserModalOpen(false);
      userForm.resetFields();
      fetchUsers();
    } catch (err) {
      if (err.errorFields) return;
      message.error("Failed to save user");
    } finally {
      setUserModalLoading(false);
    }
  };

  // ---- Delete user ----

  const handleDeleteUser = async (record) => {
    try {
      await deleteUser(record.id);
      message.success("User deleted successfully");
      fetchUsers();
    } catch {
      message.error("Failed to delete user");
    }
  };

  // ---- Export all login details ----

  const handleExportLoginDetails = async () => {
    setExportingLogins(true);
    try {
      const rows = [["name", "email", "phone", "company", "password"]];
      const results = await Promise.allSettled(
        users.map((u) => getUserLoginDetails(u.id).then((d) => ({ user: u, details: d })))
      );
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          const { user, details } = r.value;
          rows.push([
            user.name    || "",
            user.email   || "",
            user.phone   || "",
            user.company || "",
            details.password || "",
          ]);
        }
      });
      const csvContent = rows
        .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "user-login-details.csv";
      a.click();
      URL.revokeObjectURL(url);
      message.success("Login details exported successfully");
    } catch {
      message.error("Failed to export login details");
    } finally {
      setExportingLogins(false);
    }
  };

  // ---- Login details ----

  const showLoginDetails = async (record) => {
    setLoginUserName(record.name || record.email);
    setLoginDetails(null);
    setLoginModalOpen(true);
    setLoginLoading(true);
    try {
      const data = await getUserLoginDetails(record.id);
      setLoginDetails(data);
    } catch {
      message.error("Failed to load login details");
      setLoginModalOpen(false);
    } finally {
      setLoginLoading(false);
    }
  };

  // ---- Table columns ----

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Login Details">
            <Button
              size="small"
              icon={<KeyOutlined />}
              onClick={() => showLoginDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          {isAdmin && (
            <Popconfirm
              title="Delete this user?"
              description="This action cannot be undone."
              onConfirm={() => handleDeleteUser(record)}
              okText="Delete"
              okType="danger"
            >
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="users-container">
      {/* Header */}
      <div className="users-header">
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} className="users-title">
              Users
            </Title>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<DownloadOutlined />}
                loading={exportingLogins}
                onClick={handleExportLoginDetails}
                disabled={users.length === 0}
              >
                Export Logins
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Add User
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Table card */}
      <Card className="users-card">
        <div className="table-controls">
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder="Search by name, email, phone, or company…"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="users-search"
              />
            </Col>
          </Row>
        </div>

        <Table
          className="users-table"
          columns={columns}
          dataSource={filteredUsers}
          loading={loading}
          size="middle"
          scroll={{ x: "max-content" }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"] }}
        />
      </Card>

      {/* ========== CREATE / EDIT MODAL ========== */}
      <Modal
        title={editingUser ? "Edit User" : "Add User"}
        open={userModalOpen}
        onCancel={() => {
          setUserModalOpen(false);
          userForm.resetFields();
        }}
        onOk={handleUserSubmit}
        okText={editingUser ? "Save Changes" : "Create User"}
        confirmLoading={userModalLoading}
        destroyOnClose
        width={480}
      >
        <Form
          form={userForm}
          layout="vertical"
          className="users-form"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email address" },
            ]}
          >
            <Input
              placeholder="user@example.com"
              disabled={!!editingUser}
            />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="+1 (555) 000-0000" />
          </Form.Item>
          <Form.Item name="company" label="Company">
            <Input placeholder="Company name" />
          </Form.Item>
          {!editingUser && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              A password will be automatically generated and can be retrieved via
              the Login Details button after creation.
            </Text>
          )}
        </Form>
      </Modal>

      {/* ========== LOGIN DETAILS MODAL ========== */}
      <Modal
        title={`Login Details — ${loginUserName}`}
        open={loginModalOpen}
        onCancel={() => setLoginModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setLoginModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={400}
        destroyOnClose
      >
        {loginLoading ? (
          <div className="login-loading">
            <Spin />
          </div>
        ) : loginDetails ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Email">{loginDetails.email}</Descriptions.Item>
            <Descriptions.Item label="Password">{loginDetails.password}</Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>
    </div>
  );
};

export default Users;
